import { useState, useCallback, useEffect } from 'react';

/**
 * Custom hook for managing collapse state of multiple JobCards
 * 
 * @param {Array} jobs - Array of job objects
 * @param {boolean} initiallyCollapsed - Whether jobs should start collapsed (default: true)
 * @returns {Object} - Collapse management utilities
 */
export const useJobCardCollapse = (jobs = [], initiallyCollapsed = true) => {
  const [collapsedStates, setCollapsedStates] = useState({});
  const [hasInitialized, setHasInitialized] = useState(false);

  // Initialize collapsed states when jobs first load
  useEffect(() => {
    if (!hasInitialized && jobs.length > 0 && initiallyCollapsed) {
      const initialStates = {};
      jobs.forEach(job => {
        initialStates[job.id] = true;
      });
      setCollapsedStates(initialStates);
      setHasInitialized(true);
    }
  }, [jobs, initiallyCollapsed, hasInitialized]);

  // Handle individual job card collapse change
  const handleCollapseChange = useCallback((jobId, isCollapsed) => {
    setCollapsedStates(prev => ({
      ...prev,
      [jobId]: isCollapsed
    }));
  }, []);

  // Check if all jobs are collapsed
  const areAllCollapsed = useCallback(() => {
    if (jobs.length === 0) return false;
    return jobs.every(job => collapsedStates[job.id] === true);
  }, [jobs, collapsedStates]);

  // Check if any jobs are collapsed
  const areAnyCollapsed = useCallback(() => {
    return jobs.some(job => collapsedStates[job.id] === true);
  }, [jobs, collapsedStates]);

  // Toggle all job cards collapse state
  const toggleAllCollapse = useCallback(() => {
    const allCollapsed = areAllCollapsed();
    
    if (allCollapsed) {
      // Expand all
      setCollapsedStates(prev => {
        const newState = { ...prev };
        jobs.forEach(job => {
          delete newState[job.id];
        });
        return newState;
      });
    } else {
      // Collapse all
      setCollapsedStates(prev => {
        const newState = { ...prev };
        jobs.forEach(job => {
          newState[job.id] = true;
        });
        return newState;
      });
    }
  }, [jobs, areAllCollapsed]);

  // Expand all job cards
  const expandAll = useCallback(() => {
    setCollapsedStates(prev => {
      const newState = { ...prev };
      jobs.forEach(job => {
        delete newState[job.id];
      });
      return newState;
    });
  }, [jobs]);

  // Collapse all job cards
  const collapseAll = useCallback(() => {
    setCollapsedStates(prev => {
      const newState = { ...prev };
      jobs.forEach(job => {
        newState[job.id] = true;
      });
      return newState;
    });
  }, [jobs]);

  // Get collapse state for a specific job
  const isJobCollapsed = useCallback((jobId) => {
    return collapsedStates[jobId] === true;
  }, [collapsedStates]);

  // Reset all collapse states
  const resetCollapseStates = useCallback(() => {
    setCollapsedStates({});
  }, []);

  return {
    collapsedStates,
    handleCollapseChange,
    areAllCollapsed: areAllCollapsed(),
    areAnyCollapsed: areAnyCollapsed(),
    toggleAllCollapse,
    expandAll,
    collapseAll,
    isJobCollapsed,
    resetCollapseStates
  };
};

export default useJobCardCollapse;
